/**
 * Camada 0 · reduzir imagem no navegador, para que NENHUM anexo seja recusado por tamanho.
 *
 * POR QUE ELA EXISTE, e a frase e dele, em 07/09/2026:
 *
 *   "QUANDO O USUARIO NAO ENCONTRAR E FOR MANDAR A MENSAGEM TEM QUE PODER COLOCAR ANEXO,
 *    QQ TIPO DE IMAGEM DE QQ TAMANHO."
 *   "NAO QUERO GERAR NENHUM TIPO DE RESTRICAO PARA USUARIO. PRECISO QUE ELE FIQUE CONOSCO"
 *
 * A TENSAO ENTRE AS DUAS FRASES E REAL, e esta funcao existe para resolve-la. "Qualquer
 * tamanho" e um teto tecnico que nao existe: o banco tem CHECK de 50 MB (migration 0053),
 * o corpo da requisicao tem limite, e base64 infla o arquivo em 4/3. O que se pode fazer,
 * e e o que ele quer, e a pessoa NUNCA ENCONTRAR a parede: ela escolhe a foto, e o
 * navegador reduz ate caber, sem perguntar nada.
 *
 * A ALTERNATIVA, QUE ELE RECUSOU, era mostrar "arquivo grande demais, escolha outro". Isso
 * transfere para a pessoa um trabalho que a maquina faz melhor, e no meio de um pedido de
 * ajuda, que e justamente quando ela ja esta com um problema.
 *
 * ---------------------------------------------------------------------------
 * A ORDEM DA REDUCAO, e cada passo tem uma razao
 * ---------------------------------------------------------------------------
 *   1. DIMENSAO primeiro, ate 2400px na maior lateral. E o corte barato: uma foto de
 *      celular tem 4000px de largura, e nenhuma tela de suporte mostra isso.
 *   2. QUALIDADE depois, em JPEG, descendo de 0.92 ate 0.5. E onde o ganho de peso e
 *      grande e o custo visual ainda e pequeno.
 *   3. DIMENSAO DE NOVO, por ultimo, em passos de 20%. E o que custa legibilidade de texto
 *      pequeno, e por isso vem depois de tudo.
 *
 * A MESMA ORDEM que o ambiente dele ja usa para reduzir captura de tela antes de colar no
 * chat, registrada no CLAUDE geral desde 28/08/2026.
 */

/** O teto do banco, da migration 0053: 50 MB em BYTES do arquivo. */
export const TETO_ANEXO_BYTES = 52_428_800;

/**
 * O teto que a REDUCAO persegue, e ele e menor que o do banco de proposito.
 *
 * base64 infla 4/3, entao 50 MB de arquivo viram cerca de 66,7 MB de texto no corpo da
 * requisicao. Mirar em 36 MB deixa o base64 em cerca de 48 MB, com folga para o resto do
 * JSON. Mirar nos 50 cheios faria o corpo estourar DEPOIS da reducao, que e o pior lugar
 * para descobrir: a pessoa esperou o processamento inteiro para receber um erro.
 */
const ALVO_BYTES = 36 * 1024 * 1024;

/** A maior lateral no primeiro corte. Acima disso nao ha tela que mostre a diferenca. */
const LADO_MAXIMO = 2400;

export interface ImagemReduzida {
  /** O conteudo em base64, SEM o prefixo `data:`, que e o que a rota de anexo espera. */
  readonly base64: string;
  /** O tipo final. Pode DIFERIR do original: HEIC vira JPEG, porque o canvas nao exporta HEIC. */
  readonly tipoConteudo: string;
  /** O nome final. Ganha a extensao nova quando o tipo mudou, senao o arquivo baixa errado. */
  readonly nomeArquivo: string;
  /** O tamanho em bytes DEPOIS da reducao. */
  readonly tamanhoBytes: number;
  /** Quantos bytes tinha antes. Serve para a tela dizer que reduziu, quando reduziu. */
  readonly tamanhoOriginalBytes: number;
}

/** Le o arquivo como base64, sem o prefixo `data:...;base64,`. */
function lerComoBase64(arquivo: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onerror = () => reject(new Error('Não foi possível ler o arquivo.'));
    leitor.onload = () => {
      const r = String(leitor.result ?? '');
      // O corte na virgula tira o prefixo. Ele SEMPRE existe em readAsDataURL, e mesmo
      // assim ha queda: um prefixo ausente com corte cego devolveria vazio, e anexo vazio
      // e pior que anexo grande.
      const i = r.indexOf(',');
      resolve(i >= 0 ? r.slice(i + 1) : r);
    };
    leitor.readAsDataURL(arquivo);
  });
}

/** Quantos bytes uma string base64 representa. */
function bytesDoBase64(base64: string): number {
  const enchimento = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.floor((base64.length * 3) / 4) - enchimento;
}

/** Desenha num canvas na escala pedida e exporta no tipo e qualidade pedidos. */
function desenhar(
  fonte: CanvasImageSource,
  largura: number,
  altura: number,
  tipo: string,
  qualidade: number,
): Promise<Blob | null> {
  const tela = document.createElement('canvas');
  tela.width = Math.max(1, Math.round(largura));
  tela.height = Math.max(1, Math.round(altura));
  const ctx = tela.getContext('2d');
  if (!ctx) return Promise.resolve(null);
  // O fundo BRANCO entra antes do desenho porque JPEG nao tem transparencia: sem ele, um
  // PNG com fundo transparente virava um retangulo PRETO ao ser convertido, e a pessoa
  // receberia de volta uma imagem que nao e a dela.
  if (tipo === 'image/jpeg') {
    // EXCECAO DECLARADA A `no-literal-colors`, e o motivo e que aqui branco NAO e cor de marca:
    // e o fundo que o formato exige por nao ter transparencia, e este pixel nao pinta interface
    // nenhuma. Token de tema nao serve: em tema escuro o fundo viraria escuro, e a foto que a
    // pessoa mandou voltaria com uma moldura que ela nao pediu. Medido em 12/09/2026, quando este
    // erro, junto dos nove do rastro do cursor, segurava o portao de lint da esteira.
    // eslint-disable-next-line complexity/no-literal-colors -- fundo tecnico do JPEG, ver acima
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, tela.width, tela.height);
  }
  ctx.drawImage(fonte, 0, 0, tela.width, tela.height);
  return new Promise((resolve) => tela.toBlob((b) => resolve(b), tipo, qualidade));
}

/**
 * Carrega o arquivo como algo que o canvas saiba desenhar.
 *
 * `createImageBitmap` VEM PRIMEIRO porque ele decodifica formatos que a tag de imagem pode
 * recusar, e nao depende de URL de objeto. A tag fica como queda para navegador que nao o
 * tenha.
 */
async function carregar(arquivo: File): Promise<CanvasImageSource> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(arquivo);
    } catch {
      // Formato que o bitmap nao decodifica cai na tag de imagem abaixo. HEIC de iPhone e
      // o caso comum, e em navegador que nao o suporta nem a tag resolve: ai a funcao
      // lanca, e quem chama decide o que dizer.
    }
  }
  const url = URL.createObjectURL(arquivo);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Este arquivo não pôde ser lido como imagem.'));
      img.src = url;
    });
  } finally {
    // Revogar SEMPRE, inclusive quando deu erro: URL de objeto que ninguem revoga segura o
    // arquivo inteiro na memoria da aba ate ela fechar.
    URL.revokeObjectURL(url);
  }
}

/** Monta o resultado, trocando a extensao quando o tipo mudou. */
async function montar(
  blob: Blob,
  original: File,
  tipoSaida: string,
  originalBytes: number,
): Promise<ImagemReduzida> {
  const base64 = await lerComoBase64(blob);
  // A EXTENSAO ACOMPANHA O TIPO: um HEIC convertido para JPEG que continuasse chamando
  // foto.heic baixaria com o nome errado, e o sistema tentaria abrir com o programa errado.
  const nome =
    original.type === tipoSaida
      ? original.name
      : original.name.replace(/\.[^.]+$/, '') + (tipoSaida === 'image/png' ? '.png' : '.jpg');
  return {
    base64,
    tipoConteudo: tipoSaida,
    nomeArquivo: nome,
    tamanhoBytes: bytesDoBase64(base64),
    tamanhoOriginalBytes: originalBytes,
  };
}

/**
 * Reduz a imagem ate caber, e NUNCA recusa por tamanho.
 *
 * QUANDO ELA LANCA, e sao dois casos e nenhum e tamanho:
 *   · o arquivo nao e uma imagem que este navegador saiba decodificar;
 *   · o canvas nao esta disponivel (contexto sem DOM).
 *
 * QUANDO O ARQUIVO JA CABE, ela devolve o ORIGINAL intacto, sem passar pelo canvas: a
 * pessoa mandou um PNG de 200 KB e recebe o mesmo PNG, e nao um JPEG recomprimido.
 */
export async function reduzirImagemParaAnexo(arquivo: File): Promise<ImagemReduzida> {
  const originalBytes = arquivo.size;

  // CAMINHO CURTO: ja cabe. Nao mexer e melhor que mexer, e evita recomprimir a toa.
  if (originalBytes <= ALVO_BYTES) {
    const base64 = await lerComoBase64(arquivo);
    return {
      base64,
      tipoConteudo: arquivo.type || 'application/octet-stream',
      nomeArquivo: arquivo.name,
      tamanhoBytes: originalBytes,
      tamanhoOriginalBytes: originalBytes,
    };
  }

  const fonte = await carregar(arquivo);
  const largura0 = Number((fonte as { width?: number }).width ?? 0);
  const altura0 = Number((fonte as { height?: number }).height ?? 0);
  if (largura0 === 0 || altura0 === 0) {
    throw new Error('Este arquivo não pôde ser lido como imagem.');
  }

  // O TIPO DE SAIDA e JPEG salvo quando o original ja e PNG, e a razao e o conteudo:
  // captura de tela com texto fica melhor em PNG, e foto fica muito menor em JPEG.
  const tipoSaida = arquivo.type === 'image/png' ? 'image/png' : 'image/jpeg';

  // 1 · DIMENSAO. O corte barato, e o unico que quase nunca se percebe.
  let escala = Math.min(1, LADO_MAXIMO / Math.max(largura0, altura0));

  // 2 · QUALIDADE, do melhor para o pior. Cada volta so acontece se a anterior nao coube.
  for (const qualidade of [0.92, 0.85, 0.75, 0.65, 0.5]) {
    const blob = await desenhar(fonte, largura0 * escala, altura0 * escala, tipoSaida, qualidade);
    if (blob && blob.size <= ALVO_BYTES) {
      return montar(blob, arquivo, tipoSaida, originalBytes);
    }
  }

  // 3 · DIMENSAO DE NOVO, por ultimo, porque e o que custa legibilidade de texto pequeno.
  //     O limite de oito voltas existe para nao girar para sempre: 0,8 elevado a 8 e cerca
  //     de 17% do tamanho original, e o que nao couber depois disso nao e imagem comum.
  for (let volta = 0; volta < 8; volta += 1) {
    escala *= 0.8;
    const blob = await desenhar(fonte, largura0 * escala, altura0 * escala, tipoSaida, 0.7);
    if (blob && blob.size <= ALVO_BYTES) {
      return montar(blob, arquivo, tipoSaida, originalBytes);
    }
  }

  // 4 · A QUEDA PARA JPEG, quando o PNG nao alcancou. MEDIDO em 07/09/2026: um PNG de
  //     5000x4000 com ruido percorreu as tres etapas e parou 16% acima do alvo, porque PNG
  //     e SEM PERDA e a qualidade do canvas nao tem efeito nele. Em JPEG a mesma imagem
  //     cabe com folga.
  //
  //     ELA VEM DEPOIS e nao antes: captura de tela com texto pequeno fica muito melhor em
  //     PNG, e a maioria cabe de primeira. Trocar todo mundo para JPEG desde o inicio
  //     pioraria o caso comum para resolver o raro.
  if (tipoSaida === 'image/png') {
    let escalaJpeg = Math.min(1, LADO_MAXIMO / Math.max(largura0, altura0));
    for (const qualidade of [0.85, 0.7, 0.55]) {
      const blob = await desenhar(fonte, largura0 * escalaJpeg, altura0 * escalaJpeg, 'image/jpeg', qualidade);
      if (blob && blob.size <= ALVO_BYTES) {
        return montar(blob, arquivo, 'image/jpeg', originalBytes);
      }
    }
    for (let volta = 0; volta < 6; volta += 1) {
      escalaJpeg *= 0.8;
      const blob = await desenhar(fonte, largura0 * escalaJpeg, altura0 * escalaJpeg, 'image/jpeg', 0.7);
      if (blob && blob.size <= ALVO_BYTES) {
        return montar(blob, arquivo, 'image/jpeg', originalBytes);
      }
    }
    escala = escalaJpeg;
  }

  // Ultimo recurso: o menor que a funcao consegue produzir, sempre em JPEG, que e o formato
  // que comprime. Ela DEVOLVE, e nao recusa: mandar uma imagem pequena demais e melhor que
  // nao mandar nada, e a alternativa seria exatamente a parede que ele nao quer.
  const ultimo = await desenhar(fonte, largura0 * escala, altura0 * escala, 'image/jpeg', 0.4);
  if (!ultimo) throw new Error('Não foi possível processar esta imagem.');
  return montar(ultimo, arquivo, 'image/jpeg', originalBytes);
}
